import { useEffect } from "react";
import { useDispatch } from "react-redux";

import {
  patchData,
  patchSummary,
  setRoleId,
} from "../app/offerWizard/offerWizardSlice";
import {
  useGetRoleUsersQuery,
  useGetUserRolesQuery,
} from "../app/services/roles";
import type { OfferWizardEntry } from "../components/offer-wizard/types";

interface UseOfferWizardSelectionConsistencyArgs {
  threadKey: string;
  entry: OfferWizardEntry | undefined;
}

/**
 * オファー作成ウィザードで「送信先ユーザー ⇄ ロール」の組み合わせが
 * フィルタ条件として整合するように、片方が更新された結果もう片方が
 * 候補集合から外れたタイミングで自動的にクリアする副作用フック。
 *
 *  - 受信者 → ロール: 選択中の受信者ユーザーが現 `roleId` を担えない場合、
 *    `roleId` をクリアして関連サマリ / フォーム状態をリセットする。
 *  - ロール → 受信者: `stepOrder === "role_first"` のとき、選択中のロールを
 *    アクティブにしていない受信者ユーザーが残っていれば、受信者を未選択化する。
 *    判定対象は「フォロー中ユーザー ∪ accepting_offers が ON のユーザー」で、
 *    両クエリの結果が揃ってから判定する（片方のみで判定すると誤解除し得る）。
 *
 * `serverDmId` 確定前のローカルドラフト段階で整合性を保つための副作用。
 * 確定後は autosave の差分送信で BE に反映される。
 */
export const useOfferWizardSelectionConsistency = ({
  threadKey,
  entry,
}: UseOfferWizardSelectionConsistencyArgs): void => {
  const dispatch = useDispatch();

  // ── 受信者 → ロール の整合性 ────────────────────────────────
  const recipient = entry?.data.recipient;
  const recipientUserId =
    recipient?.mode === "follows" ? recipient.userId : undefined;
  const currentRoleId = entry?.data.roleId;

  const { data: userRolesData } = useGetUserRolesQuery(
    recipientUserId as number,
    { skip: recipientUserId === undefined || currentRoleId === undefined },
  );

  useEffect(() => {
    if (recipientUserId === undefined) return;
    if (currentRoleId === undefined) return;
    const roles = userRolesData?.data?.roles;
    if (!roles) return; // 取得待ち
    if (roles.some((r) => r.id === currentRoleId)) return;

    dispatch(setRoleId({ threadKey, roleId: undefined }));
    dispatch(patchSummary({ threadKey, patch: { roleLabel: undefined } }));
    dispatch(
      patchData({
        threadKey,
        patch: { form: { allowSubcontracting: false } },
      }),
    );
  }, [
    recipientUserId,
    currentRoleId,
    userRolesData,
    dispatch,
    threadKey,
  ]);

  // ── ロール → 受信者 の整合性（role_first 時のみ） ──────────────
  const isRoleFirst = entry?.stepOrder === "role_first";
  const roleIdForRecipientCheck = isRoleFirst ? currentRoleId : undefined;
  const skipRoleUserChecks =
    roleIdForRecipientCheck === undefined ||
    recipient?.mode !== "follows" ||
    recipientUserId === undefined;

  const { data: roleFollowingData } = useGetRoleUsersQuery(
    {
      role_id: roleIdForRecipientCheck as number,
      following: true,
      limit: 100,
    },
    { skip: skipRoleUserChecks },
  );
  const { data: roleAcceptingData } = useGetRoleUsersQuery(
    {
      role_id: roleIdForRecipientCheck as number,
      accepting_offers: true,
      limit: 100,
    },
    { skip: skipRoleUserChecks },
  );

  useEffect(() => {
    if (skipRoleUserChecks) return;
    const followingUsers = roleFollowingData?.data?.users;
    const acceptingUsers = roleAcceptingData?.data?.users;
    if (!followingUsers || !acceptingUsers) return; // 両クエリの完了を待つ
    const allowed = new Set<number>();
    for (const u of followingUsers) allowed.add(u.id);
    for (const u of acceptingUsers) allowed.add(u.id);
    if (recipientUserId !== undefined && allowed.has(recipientUserId)) return;

    dispatch(
      patchData({ threadKey, patch: { recipient: { mode: "follows" } } }),
    );
    dispatch(
      patchSummary({
        threadKey,
        patch: {
          recipientLabel: undefined,
          recipientAvatarUrl: undefined,
          recipientName: undefined,
        },
      }),
    );
  }, [
    skipRoleUserChecks,
    recipientUserId,
    roleFollowingData,
    roleAcceptingData,
    dispatch,
    threadKey,
  ]);
};
